import './app-button.ts';
import './app-icon.ts';

export class AppTimePicker extends HTMLElement {
    private _value: string = '5m';
    private _showCustomPicker = false;
    private _customRange = { start: '', end: '' };

    static get observedAttributes() {
        return ['value'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        
        const now = new Date();
        const tz = now.getTimezoneOffset() * 60000;
        this._customRange = {
            start: new Date(now.getTime() - 3600000 - tz).toISOString().slice(0, 16),
            end: new Date(now.getTime() - tz).toISOString().slice(0, 16)
        };
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;
        if (name === 'value') {
            this._value = newValue || '5m';
            this.render();
        }
    }

    connectedCallback() {
        if (!this.hasAttribute('value')) {
            this.setAttribute('value', this._value);
        }
        
        try {
            const saved = localStorage.getItem('crate_custom_range');
            if (saved) {
                this._customRange = JSON.parse(saved);
            }
        } catch (e) {}

        this.render();
    }

    private selectRange(r: string) {
        if (r === 'custom') {
            this._showCustomPicker = !this._showCustomPicker;
            this.render();
            return;
        }
        
        if (this._value !== r) {
            this._value = r;
            this.setAttribute('value', r);
            this.dispatchEvent(new CustomEvent('range-change', {
                detail: { range: r },
                bubbles: true,
                composed: true
            }));
            this._showCustomPicker = false;
            this.render();
        }
    }

    private applyCustomRange() {
        this._value = 'custom';
        this.setAttribute('value', 'custom');
        this._showCustomPicker = false;
        
        localStorage.setItem('crate_custom_range', JSON.stringify(this._customRange));

        this.dispatchEvent(new CustomEvent('range-change', {
            detail: { range: 'custom', customRange: this._customRange },
            bubbles: true,
            composed: true
        }));
        this.render();
    }

    private render() {
        if (!this.shadowRoot) return;

        const ranges = ['5m', '30m', '1h', '24h', '7d', '30d'];

        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: inline-block;
                }
                .range-selector {
                    display: flex;
                    align-items: center;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 2px;
                    border-radius: 8px;
                    gap: 2px;
                    height: 32px;
                    box-sizing: border-box;
                    flex-shrink: 0;
                    position: relative;
                }
                .range-btn {
                    font-size: 0.65rem;
                    font-weight: 600;
                    font-family: var(--font-mono, monospace);
                    color: var(--text-dim, #888);
                    background: transparent;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 32px;
                    height: 100%;
                    padding: 0 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .range-btn:hover {
                    color: white;
                    background: rgba(255,255,255,0.05);
                }
                .range-btn.active {
                    color: var(--text-main, #fff);
                    background: var(--accent, #f59e0b);
                }
            </style>
            <div style="position: relative; display: flex; align-items: center;">
                <div class="range-selector">
                    ${ranges.map(r => `
                        <button 
                            class="range-btn ${this._value === r ? 'active' : ''}" 
                            data-range="${r}"
                        >${r.toUpperCase()}</button>
                    `).join('')}
                    <button 
                        class="range-btn ${this._value === 'custom' ? 'active' : ''}" 
                        data-range="custom"
                    ><app-icon name="edit" size="12"></app-icon></button>
                </div>

                ${this._showCustomPicker ? `
                    <div class="custom-range-picker" style="position: absolute; top: calc(100% + 8px); right: 0; background: #1a1a1a; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 1rem; z-index: 1000; width: 300px; box-shadow: 0 16px 40px rgba(0,0,0,0.6); display: flex; flex-direction: column; gap: 0.75rem;">
                        <div style="display: flex; flex-direction: column; gap: 0.35rem;">
                            <label style="font-size: 0.6rem; text-transform: uppercase; color: var(--accent, #f59e0b); font-weight: 700;">Start Date</label>
                            <input type="datetime-local" class="datetime-input" style="width: 100%; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.08); color: white; padding: 0.4rem; border-radius: 6px; font-size: 0.8rem; color-scheme: dark;" value="${this._customRange.start}" data-field="start">
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 0.35rem;">
                            <label style="font-size: 0.6rem; text-transform: uppercase; color: var(--accent, #f59e0b); font-weight: 700;">End Date</label>
                            <input type="datetime-local" class="datetime-input" style="width: 100%; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.08); color: white; padding: 0.4rem; border-radius: 6px; font-size: 0.8rem; color-scheme: dark;" value="${this._customRange.end}" data-field="end">
                        </div>
                        <div style="display: flex; justify-content: flex-end; gap: 0.5rem; padding-top: 0.5rem; border-top: 1px solid rgba(255,255,255,0.05);">
                            <app-button variant="ghost" size="sm" class="cancel-range-btn">Cancel</app-button>
                            <app-button variant="primary" size="sm" class="apply-range-btn">Apply</app-button>
                        </div>
                    </div>
                ` : ''}
            </div>
        `;

        this.shadowRoot.querySelectorAll('.range-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const r = (e.currentTarget as HTMLElement).getAttribute('data-range');
                if (r) this.selectRange(r);
            });
        });

        if (this._showCustomPicker) {
            this.shadowRoot.querySelector('.apply-range-btn')?.addEventListener('click', () => {
                this.applyCustomRange();
            });
            this.shadowRoot.querySelector('.cancel-range-btn')?.addEventListener('click', () => {
                this._showCustomPicker = false;
                this.render();
            });
            this.shadowRoot.querySelectorAll('.datetime-input').forEach(input => {
                input.addEventListener('change', (e: any) => {
                    const field = input.getAttribute('data-field');
                    if (field === 'start') this._customRange.start = e.target.value;
                    if (field === 'end') this._customRange.end = e.target.value;
                });
            });
        }
    }
}

if (!customElements.get('app-time-picker')) {
    customElements.define('app-time-picker', AppTimePicker);
}
